import { useState, useEffect, useRef, useCallback } from 'react';
import {
  motionOptions,
  aspectOptions,
  VIDEO_AD_GENERATE_CREDITS,
  VIDEO_AD_REGENERATE_CREDITS,
  VIDEO_AD_DURATION,
  VIDEO_AD_EDGE_URL,
  type VideoAdState,
} from '@/config/videoAd';
import { Link, useNavigate } from 'react-router-dom';
import { downloadFile } from '@/lib/download';
import { getEdgeFunctionHeaders } from '@/lib/edgeFunctions';

const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 45; // ~3 minutes max

/* ─── Spinner ──────────────────────────────────────────────────────────── */
function Spinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sz = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-10 h-10' : 'w-6 h-6';
  return (
    <div className={`${sz} flex items-center justify-center`}>
      <i className="ri-loader-4-line animate-spin text-current" />
    </div>
  );
}

/* ─── Props ────────────────────────────────────────────────────────────── */
interface VideoAdModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceImageUrl: string;
  creditsRemaining: number;
  hasEnoughCredits: (cost: number) => boolean;
  onCreditsRefetch: () => Promise<void>;
  generationId?: string;
}

/* ─── VideoAdModal ─────────────────────────────────────────────────────── */
export default function VideoAdModal({
  isOpen,
  onClose,
  sourceImageUrl,
  creditsRemaining,
  hasEnoughCredits,
  onCreditsRefetch,
  generationId,
}: VideoAdModalProps) {
  const navigate = useNavigate();

  const [selectedMotion, setSelectedMotion] = useState<'morph' | 'dynamic' | 'cinematic'>('dynamic');
  const [selectedAspect, setSelectedAspect] = useState<'vertical' | 'square' | 'landscape'>('vertical');
  const [creativePrompt, setCreativePrompt] = useState('');
  const [videoState, setVideoState] = useState<VideoAdState>({
    status: 'idle',
    taskId: null,
    videoUrl: null,
    audioUrl: null,
    voiceoverScript: null,
    error: null,
    progress: null,
  });
  const [isRegenerate, setIsRegenerate] = useState(false);

  const pollCountRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset to idle when modal closes
  useEffect(() => {
    if (!isOpen) {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      // Keep video result visible on re-open if completed
      if (videoState.status !== 'completed') {
        setVideoState({ status: 'idle', taskId: null, videoUrl: null, audioUrl: null, voiceoverScript: null, error: null, progress: null });
      }
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const callEdge = useCallback(async (payload: Record<string, unknown>): Promise<Record<string, unknown>> => {
    const res = await fetch(VIDEO_AD_EDGE_URL, {
      method: 'POST',
      headers: await getEdgeFunctionHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await res.json() as Record<string, unknown>;
    if (!res.ok || data.error) throw new Error(typeof data.error === 'string' ? data.error : `Request failed (${res.status})`);
    return data;
  }, []);

  const pollTask = useCallback(async (taskId: string) => {
    if (pollCountRef.current >= MAX_POLLS) {
      setVideoState((prev) => ({
        ...prev,
        status: 'failed',
        error: 'Video generation timed out. Please try again.',
      }));
      return;
    }

    try {
      const data = await callEdge({ action: 'poll', taskId, generationId });
      const status = data.status as string;

      if (status === 'completed') {
        setVideoState({
          status: 'completed',
          taskId,
          videoUrl: data.videoUrl as string,
          audioUrl: typeof data.audioUrl === 'string' ? data.audioUrl : null,
          voiceoverScript: typeof data.voiceoverScript === 'string' ? data.voiceoverScript : null,
          error: null,
          progress: 100,
        });
        await onCreditsRefetch();
      } else if (status === 'failed') {
        setVideoState((prev) => ({
          ...prev,
          status: 'failed',
          error: 'Video generation failed. Please try again.',
        }));
      } else {
        pollCountRef.current += 1;
        const progress = typeof data.progress === 'number' ? data.progress : null;
        setVideoState((prev) => ({ ...prev, status: 'processing', progress }));
        pollTimerRef.current = setTimeout(() => pollTask(taskId), POLL_INTERVAL_MS);
      }
    } catch (err) {
      setVideoState((prev) => ({
        ...prev,
        status: 'failed',
        error: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      }));
    }
  }, [callEdge, onCreditsRefetch, generationId]);

  const handleGenerate = useCallback(async (creditCost: number) => {
    if (!sourceImageUrl) return;

    const isRegen = isRegenerate;

    setVideoState({ status: 'pending', taskId: null, videoUrl: null, audioUrl: null, voiceoverScript: null, error: null, progress: null });
    pollCountRef.current = 0;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);

    try {
      const data = await callEdge({
        action: 'create',
        sourceImageUrl,
        motionStyle: selectedMotion,
        aspectRatio: selectedAspect,
        duration: VIDEO_AD_DURATION,
        provider: 'runway',
        generationType: 'video_ad',
        generationId,
        ...(creativePrompt.trim() ? { creativePrompt: creativePrompt.trim() } : {}),
      });

      const taskId = data.taskId as string;
      setVideoState((prev) => ({ ...prev, status: 'processing', taskId }));

      if (!isRegen) setIsRegenerate(true);

      pollTimerRef.current = setTimeout(() => pollTask(taskId), POLL_INTERVAL_MS);
    } catch (err) {
      if (err instanceof Error && err.message.includes('Insufficient')) {
        setVideoState({
          status: 'failed',
          taskId: null,
          videoUrl: null,
          audioUrl: null,
          voiceoverScript: null,
          error: 'Insufficient credits. Please upgrade or purchase more credits.',
          progress: null,
        });
      } else {
        setVideoState({
          status: 'failed',
          taskId: null,
          videoUrl: null,
          audioUrl: null,
          voiceoverScript: null,
          error: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
          progress: null,
        });
      }
    }
  }, [sourceImageUrl, selectedMotion, selectedAspect, callEdge, pollTask, isRegenerate, generationId]);

  const handleReset = () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    setVideoState({ status: 'idle', taskId: null, videoUrl: null, audioUrl: null, voiceoverScript: null, error: null, progress: null });
  };

  const handleDownloadVideo = () => {
    if (!videoState.videoUrl) return;
    downloadFile(videoState.videoUrl, `video-ad-${Date.now()}.mp4`);
  };

  const creditCost = isRegenerate ? VIDEO_AD_REGENERATE_CREDITS : VIDEO_AD_GENERATE_CREDITS;
  const canAfford = hasEnoughCredits(creditCost);
  const isActive = videoState.status === 'pending' || videoState.status === 'processing';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => !isActive && onClose()}
      />

      {/* Modal panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-stone-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-orange-400 shrink-0">
              <i className="ri-film-fill text-white text-base" />
            </div>
            <div>
              <h2 className="text-sm font-extrabold text-stone-900 leading-tight">Animate This Ad</h2>
              <p className="text-xs text-stone-400 mt-0.5">Turn your image into a short video ad</p>
            </div>
          </div>
          {!isActive && (
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 px-6 py-5 space-y-5">

          {/* ── Source image thumbnail ── */}
          <div className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-xl p-3">
            <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-stone-200">
              <img src={sourceImageUrl} alt="Source ad" className="w-full h-full object-cover object-top" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-stone-800">Selected Ad Creative</p>
              <p className="text-xs text-stone-400 mt-0.5">This image will be animated into a video ad</p>
            </div>
            <div className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-green-100">
              <i className="ri-check-line text-green-600 text-xs" />
            </div>
          </div>

          {/* ── Processing / Completed / Failed states ── */}
          {videoState.status === 'pending' || videoState.status === 'processing' ? (
            <ProcessingView progress={videoState.progress} />
          ) : videoState.status === 'completed' && videoState.videoUrl ? (
            <CompletedView
              videoUrl={videoState.videoUrl}
              motionLabel={motionOptions.find((m) => m.id === selectedMotion)?.label ?? ''}
              aspectLabel={aspectOptions.find((a) => a.id === selectedAspect)?.label ?? ''}
              aspectRatio={aspectOptions.find((a) => a.id === selectedAspect)?.ratio ?? '9:16'}
              onClose={onClose}
              onNavigateToEditor={() => {
                onClose();
                const arMap: Record<string, string> = { vertical: '9:16', square: '1:1', landscape: '16:9' };
                navigate('/dashboard/video-editor', {
                  state: {
                    videoUrl: videoState.videoUrl,
                    audioUrl: videoState.audioUrl,
                    voiceoverScript: videoState.voiceoverScript,
                    aspectRatio: arMap[selectedAspect] ?? '9:16',
                    videoId: String(Date.now()),
                  },
                });
              }}
              onDownload={handleDownloadVideo}
              onRegenerate={() => handleGenerate(VIDEO_AD_REGENERATE_CREDITS)}
              onBack={handleReset}
              canAffordRegen={hasEnoughCredits(VIDEO_AD_REGENERATE_CREDITS)}
              creditsRemaining={creditsRemaining}
            />
          ) : videoState.status === 'failed' ? (
            <FailedView
              error={videoState.error}
              onRetry={() => handleGenerate(creditCost)}
              onBack={handleReset}
              canAfford={canAfford}
            />
          ) : (
            <>
              {/* ── Motion Style ── */}
              <div>
                <label className="block text-xs font-bold text-stone-700 uppercase tracking-widest mb-3">
                  Motion Style
                </label>
                <div className="space-y-2">
                  {motionOptions.map((opt) => {
                    const isSelected = selectedMotion === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setSelectedMotion(opt.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                          isSelected
                            ? 'border-orange-400 bg-orange-50'
                            : 'border-stone-200 bg-stone-50 hover:border-orange-200 hover:bg-orange-50/40'
                        }`}
                      >
                        <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected ? 'border-orange-500 bg-orange-500' : 'border-stone-300'
                        }`}>
                          {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        <div className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                          isSelected ? 'bg-orange-500 text-white' : 'bg-stone-200 text-stone-500'
                        }`}>
                          <i className={`${opt.icon} text-sm`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold ${isSelected ? 'text-orange-800' : 'text-stone-800'}`}>
                              {opt.label}
                            </span>
                            {opt.badge && (
                              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                                opt.badge === 'Premium'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-orange-100 text-orange-700'
                              }`}>
                                {opt.badge}
                              </span>
                            )}
                          </div>
                          <p className={`text-xs mt-0.5 leading-tight ${isSelected ? 'text-orange-500' : 'text-stone-400'}`}>
                            {opt.description}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Aspect Ratio ── */}
              <div>
                <label className="block text-xs font-bold text-stone-700 uppercase tracking-widest mb-3">
                  Aspect Ratio
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {aspectOptions.map((opt) => {
                    const isSelected = selectedAspect === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setSelectedAspect(opt.id)}
                        className={`relative flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                          isSelected
                            ? 'border-orange-400 bg-orange-50'
                            : 'border-stone-200 bg-stone-50 hover:border-orange-200 hover:bg-orange-50/40'
                        }`}
                      >
                        {opt.recommended && (
                          <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs font-bold bg-orange-500 text-white px-2 py-0.5 rounded-full whitespace-nowrap">
                            Recommended
                          </span>
                        )}
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                          isSelected ? 'bg-orange-500 text-white' : 'bg-stone-200 text-stone-500'
                        }`}>
                          <i className={`${opt.icon} text-sm`} />
                        </div>
                        <span className={`text-xs font-extrabold ${isSelected ? 'text-orange-700' : 'text-stone-700'}`}>
                          {opt.ratio}
                        </span>
                        <span className={`text-xs font-medium ${isSelected ? 'text-orange-500' : 'text-stone-400'}`}>
                          {opt.label}
                        </span>
                        <span className={`text-xs text-center leading-tight ${isSelected ? 'text-orange-400' : 'text-stone-400'}`}>
                          {opt.description}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Creative Direction ── */}
              <div>
                <label className="block text-xs font-bold text-stone-700 uppercase tracking-widest mb-1.5">
                  Creative Direction
                  <span className="ml-2 text-xs font-normal normal-case tracking-normal text-stone-400">Optional</span>
                </label>
                <p className="text-xs text-stone-400 mb-2.5 leading-relaxed">
                  Describe the mood, movement, or visual style you want — e.g. &quot;slow zoom with golden light&quot; or &quot;energetic with fast cuts and neon flashes&quot;.
                </p>
                <div className="relative">
                  <textarea
                    value={creativePrompt}
                    onChange={(e) => setCreativePrompt(e.target.value.slice(0, 300))}
                    placeholder="e.g. Slow cinematic zoom with warm golden light and soft bokeh in the background..."
                    rows={3}
                    className="w-full resize-none rounded-xl border-2 border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200 leading-relaxed"
                  />
                  <span className={`absolute bottom-2.5 right-3 text-xs tabular-nums transition-colors ${
                    creativePrompt.length >= 280 ? 'text-orange-500' : 'text-stone-300'
                  }`}>
                    {creativePrompt.length}/300
                  </span>
                </div>
                {creativePrompt.trim() && (
                  <button
                    onClick={() => setCreativePrompt('')}
                    className="mt-1.5 text-xs text-stone-400 hover:text-stone-600 transition-colors cursor-pointer flex items-center gap-1"
                  >
                    <i className="ri-close-circle-line text-xs" /> Clear prompt
                  </button>
                )}
              </div>

              {/* ── Duration (locked) ── */}
              <div className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-xl px-4 py-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-200 text-stone-500 shrink-0">
                  <i className="ri-timer-line text-sm" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-stone-800">Duration — {VIDEO_AD_DURATION} seconds</p>
                  <p className="text-xs text-stone-400 mt-0.5">Fixed duration optimised for social ad placements</p>
                </div>
                <div className="shrink-0 flex items-center gap-1 text-xs text-stone-400 bg-stone-200 px-2 py-1 rounded-full">
                  <i className="ri-lock-line text-xs" /> Fixed
                </div>
              </div>

              {/* ── Credit cost + balance ── */}
              <div className="bg-gradient-to-r from-stone-900 to-stone-800 rounded-xl px-4 py-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="ri-coin-line text-amber-400 text-base" />
                    <span className="text-sm font-bold text-white">
                      {isRegenerate ? 'Regenerate Video Ad' : 'Generate Video Ad'}
                    </span>
                  </div>
                  <span className={`text-base font-extrabold ${canAfford ? 'text-amber-400' : 'text-red-400'}`}>
                    {creditCost} credits
                  </span>
                </div>
                <div className="h-px bg-white/10" />
                <div className="flex items-center justify-between text-xs">
                  <span className="text-stone-400">Your current balance</span>
                  <span className={`font-bold ${canAfford ? 'text-stone-200' : 'text-red-400'}`}>
                    {creditsRemaining} credits
                  </span>
                </div>
                {!canAfford && (
                  <p className="text-xs text-red-300 flex items-center gap-1.5">
                    <i className="ri-error-warning-line shrink-0" />
                    Not enough credits. Please upgrade or purchase more credits.
                  </p>
                )}
              </div>

              {/* ── CTA button ── */}
              <button
                onClick={() => handleGenerate(creditCost)}
                disabled={!canAfford}
                className={`w-full flex items-center justify-center gap-2.5 py-4 rounded-xl font-bold text-sm transition-all duration-200 whitespace-nowrap ${
                  canAfford
                    ? 'bg-gradient-to-r from-rose-500 via-orange-500 to-amber-400 text-white hover:from-rose-600 hover:via-orange-600 hover:to-amber-500 cursor-pointer'
                    : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-film-line text-base" />
                {isRegenerate ? `Regenerate Video — ${VIDEO_AD_REGENERATE_CREDITS} Credits` : `Generate Video Ad — ${VIDEO_AD_GENERATE_CREDITS} Credits`}
              </button>

              {!canAfford && (
                <Link
                  to="/pricing"
                  className="flex items-center justify-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors"
                >
                  <i className="ri-flashlight-fill" /> Upgrade to get more credits
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Processing View ──────────────────────────────────────────────────── */
function ProcessingView({ progress }: { progress: number | null }) {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const t = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), 500);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-10 gap-6 text-center">
      {/* Animated icon */}
      <div className="relative">
        <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500 to-orange-400">
          <i className="ri-film-fill text-white text-3xl" />
        </div>
        <div className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full bg-white border-2 border-white flex items-center justify-center">
          <span className="w-4 h-4 rounded-full bg-orange-400 animate-ping" />
        </div>
      </div>

      <div>
        <p className="text-base font-extrabold text-stone-900">Creating your video ad{dots}</p>
        <p className="text-sm text-stone-400 mt-1.5 max-w-xs mx-auto leading-relaxed">
          Our AI is animating your image. This usually takes 1–2 minutes.
        </p>
      </div>

      {/* Progress bar */}
      {progress !== null ? (
        <div className="w-full max-w-xs space-y-1.5">
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-rose-500 to-orange-400 rounded-full transition-all duration-1000"
              style={{ width: `${Math.min(progress * 100, 99)}%` }}
            />
          </div>
          <p className="text-xs text-stone-400 text-right">{Math.round(progress * 100)}%</p>
        </div>
      ) : (
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-orange-400 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      )}

      <p className="text-xs text-stone-400 flex items-center gap-1.5">
        <i className="ri-information-line text-stone-300" />
        Please keep this window open while generating
      </p>
    </div>
  );
}

/* ─── Completed View ───────────────────────────────────────────────────── */
function CompletedView({
  videoUrl,
  motionLabel,
  aspectLabel,
  aspectRatio,
  onClose,
  onNavigateToEditor,
  onDownload,
  onRegenerate,
  onBack,
  canAffordRegen,
  creditsRemaining,
}: {
  videoUrl: string;
  motionLabel: string;
  aspectLabel: string;
  aspectRatio: string;
  onClose: () => void;
  onNavigateToEditor: () => void;
  onDownload: () => void;
  onRegenerate: () => void;
  onBack: () => void;
  canAffordRegen: boolean;
  creditsRemaining: number;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  return (
    <div className="space-y-4">
      {/* Success banner */}
      <div className="flex items-center gap-2.5 bg-green-50 border border-green-200 text-green-700 px-3 py-2.5 rounded-xl">
        <i className="ri-checkbox-circle-fill text-green-500 text-base shrink-0" />
        <span className="text-xs font-semibold flex-1">Your video ad is ready!</span>
        <span className="text-xs text-green-500">{motionLabel} · {aspectLabel} ({aspectRatio})</span>
      </div>

      {/* Video player */}
      <div className="rounded-xl overflow-hidden bg-black border border-stone-200">
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          autoPlay
          loop
          playsInline
          className="w-full max-h-72 object-contain"
        />
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={onDownload}
          className="flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-stone-900 to-stone-800 text-white text-sm font-bold hover:from-stone-800 hover:to-stone-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-download-2-line text-base" />
          Download
        </button>
        <button
          onClick={onNavigateToEditor}
          className="flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-orange-400 text-white text-sm font-bold hover:from-rose-600 hover:to-orange-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-edit-2-line text-base" />
          Edit Video
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={onRegenerate}
          disabled={!canAffordRegen}
          className={`flex flex-col items-center gap-1 py-3 rounded-xl border-2 text-xs font-bold transition-all duration-200 whitespace-nowrap ${
            canAffordRegen
              ? 'border-orange-300 text-orange-600 bg-orange-50 hover:bg-orange-100 cursor-pointer'
              : 'border-stone-200 text-stone-400 cursor-not-allowed bg-stone-50'
          }`}
        >
          <i className="ri-refresh-line text-base" />
          <span>Regenerate Video</span>
          <span className={`font-normal ${canAffordRegen ? 'text-orange-400' : 'text-stone-400'}`}>
            {VIDEO_AD_REGENERATE_CREDITS} credits
          </span>
        </button>

        <button
          onClick={onBack}
          className="flex flex-col items-center gap-1 py-3 rounded-xl border-2 border-stone-200 text-stone-600 bg-stone-50 hover:bg-stone-100 text-xs font-bold transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-base" />
          <span>Back to Results</span>
          <span className="font-normal text-stone-400">Change settings</span>
        </button>
      </div>

      {!canAffordRegen && (
        <p className="text-xs text-stone-400 text-center">
          Balance: <strong className="text-stone-600">{creditsRemaining} credits</strong> — need {VIDEO_AD_REGENERATE_CREDITS} to regenerate.{' '}
          <Link to="/pricing" className="text-orange-500 underline underline-offset-2">Upgrade</Link>
        </p>
      )}
    </div>
  );
}

/* ─── Failed View ──────────────────────────────────────────────────────── */
function FailedView({
  error,
  onRetry,
  onBack,
  canAfford,
}: {
  error: string | null;
  onRetry: () => void;
  onBack: () => void;
  canAfford: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-5 py-8 text-center">
      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-red-100">
        <i className="ri-error-warning-line text-red-500 text-2xl" />
      </div>

      <div>
        <p className="text-sm font-bold text-stone-900">Video generation failed</p>
        <p className="text-xs text-stone-400 mt-1.5 max-w-xs mx-auto leading-relaxed">
          {error ?? 'Something went wrong. Please try again.'}
        </p>
      </div>

      <div className="flex gap-3 w-full max-w-xs">
        {canAfford && (
          <button
            onClick={onRetry}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-orange-400 text-white text-sm font-bold hover:from-rose-600 hover:to-orange-500 cursor-pointer transition-all whitespace-nowrap"
          >
            <i className="ri-refresh-line" /> Try Again
          </button>
        )}
        <button
          onClick={onBack}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-stone-200 text-stone-600 text-sm font-bold hover:bg-stone-50 cursor-pointer transition-all whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" /> Go Back
        </button>
      </div>
    </div>
  );
}
